import type {
  BlockControlMethodHelpers,
  BlockStateDefinition,
  ImplicitDependencyGroup,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { SelectedPoint, BlockRefContent } from '@unifyapps/defs/blocks/Chart/types';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import type { Action } from '@unifyapps/defs/types/action';
import { lazy } from 'react';
import { BlockDataAttributes } from '@unifyapps/carbon/no-code/hooks/useBlockDataAttributes';

const importChartComponent = () =>
  import(
    /* webpackChunkName: "chart-block" */
    './Chart'
  );

const Chart = lazy(importChartComponent);

class ChartBlockStateDefinition implements BlockStateDefinition {
  useBlockRef?: boolean | undefined = true;

  get type(): ComponentTypeUnionType {
    return 'Chart';
  }

  getBlockControlMethods() {
    return [
      {
        id: 'setSelectedDataPoint' as const,
        label: 'Select Data Point',
        method: (blockState: BlockStateUnionType, action: Action) => {
          const { methodPayload } = action.payload as {
            methodPayload: SelectedPoint;
          };

          return {
            ...blockState,
            selectedPoints: [methodPayload],
          } as BlockStateUnionType;
        },
      },
      {
        id: 'clearSelectedDataPoint' as const,
        label: 'Clear Selected Data Point',
        method: (blockState: BlockStateUnionType) => {
          return {
            ...blockState,
            selectedPoints: [],
          } as BlockStateUnionType;
        },
      },
      {
        id: 'generatePreview' as const,
        label: 'Generate Preview',
        method: async (
          blockState: BlockStateUnionType,
          action: Action,
          blockRef: unknown,
          helpers: BlockControlMethodHelpers,
        ) => {
          try {
            const blockEl = document.querySelector<HTMLElement>(
              `[${BlockDataAttributes.BlockId}="${blockState.id}"]`,
            );

            if (!blockEl) {
              helpers.showSnackbar({
                title: 'Something went wrong while generating preview',
                color: 'error',
              });
              console.error('Error generating preview: ', 'Block element not found');
              return blockState;
            }

            const html2canvas = await import('html2canvas');
            const canvas = await html2canvas.default(blockEl);

            const image = canvas.toDataURL('image/png');

            return {
              ...blockState,
              preview: image,
            };
          } catch (error) {
            helpers.showSnackbar({
              title: 'Something went wrong while generating preview',
              color: 'error',
            });
            console.error('Error generating preview', error);
            return blockState;
          }
        },
      },
      {
        id: 'focusSelectedDataPoint' as const,
        label: 'Focus Selected Data Point',
        method: (
          blockState: BlockStateUnionType,
          action: Action,
          blockRef: BlockRefContent,
          helpers: BlockControlMethodHelpers,
        ) => {
          blockRef.focusSelectedDataPoint();
          return blockState;
        },
      },
      {
        id: 'resetFocussedPoint' as const,
        label: 'Reset Focused Point',
        method: (
          blockState: BlockStateUnionType,
          action: Action,
          blockRef: BlockRefContent,
          helpers: BlockControlMethodHelpers,
        ) => {
          blockRef.resetFocussedPoint();
          return blockState;
        },
      },
    ];
  }

  get initialStateGetter() {
    return (block: BlockType) =>
      ({
        id: block.id,
        ..._pick(block.component, ['content', 'appearance', 'componentType']),
      }) as BlockStateUnionType;
  }

  getComponent() {
    return Chart;
  }

  getImplicitDependencyGroups(): ImplicitDependencyGroup {
    return { isEntitySuspended: true, usingUserContext: true };
  }

  preload() {
    return {
      blockPreload: importChartComponent,
    };
  }
}

export default ChartBlockStateDefinition;
